import { arrayToggle } from 'common/ArrayUtilities';
import { THUMB_DOWN_PERCENT, THUMB_MIDDLE_PERCENT, THUMB_UP_PERCENT } from 'common/RatingUtilities';
import { ArrayType } from 'common/TypeUtilities';
import { useCallback, useMemo } from 'react';
import { UpcomingOrderFieldsFragment } from 'src/gqlReactTypings.generated.d';
import { match } from 'ts-pattern';
import { GroupOrderFoodItemRating } from '../../GroupOrderModal/GroupOrderFoodItemRating';
import { FoodItemRating, RatingPercent } from '../constants';
import { FoodItemRatingUpdate } from './constants';

interface OrderRatingSectionFoodItemProps {
  foodItem: ArrayType<UpcomingOrderFieldsFragment['lineItems']>['foodItem'];
  foodItemRating: FoodItemRating | undefined;
  hasBeenRated: boolean;
  ignoreFoodTags: string[];
  neutralFoodTags: string[];
  negativeFoodTags: string[];
  onFoodItemRatingUpdate: (update: FoodItemRatingUpdate) => void;
  orderId: number;
  positiveFoodTags: string[];
}

/**
 * Component for rating an individual line item within an order.
 */
export const OrderRatingSectionFoodItem = ({
  foodItem,
  foodItemRating,
  hasBeenRated,
  ignoreFoodTags,
  neutralFoodTags,
  negativeFoodTags,
  onFoodItemRatingUpdate,
  orderId,
  positiveFoodTags,
}: OrderRatingSectionFoodItemProps): JSX.Element => {
  const { id: foodItemId } = foodItem;

  const updateFoodItemRating = useCallback(
    (foodItemRatingUpdate: Pick<FoodItemRating, 'tags' | 'ratingPercent'> | null) => {
      const newFoodItemRating = foodItemRatingUpdate
        ? {
            ...foodItemRatingUpdate,
            foodItemId,
          }
        : null;

      onFoodItemRatingUpdate({
        orderId,
        foodItemId,
        foodItemRating: newFoodItemRating,
      });
    },
    [foodItemId, onFoodItemRatingUpdate, orderId]
  );

  /**
   * Handler for updating the food item rating.
   *
   * If the clicked rating is the same as the existing rating, remove the rating altogether.
   * Otherwise, set the rating as the clicked rating and reset the tags.
   */
  const handleRatingClick = useCallback(
    (ratingPercent: RatingPercent) => {
      match(foodItemRating?.ratingPercent)
        .with(ratingPercent, () => updateFoodItemRating(null))
        .otherwise(() =>
          updateFoodItemRating({
            ratingPercent,
            tags: [],
          })
        );
    },
    [foodItemRating?.ratingPercent, updateFoodItemRating]
  );

  /**
   * Handler for updating the food item tags.
   */
  const handleTagClick = useCallback(
    (tag: string) => {
      // User shouldn't be able to click on a tag without a rating selected,
      // so we don't actually expect this to happen.
      if (!foodItemRating) {
        throw new Error('You cannot add a dish tag before rating it');
      }

      const { ratingPercent, tags } = foodItemRating;

      updateFoodItemRating({
        ratingPercent,
        tags: arrayToggle(tags, tag),
      });
    },
    [foodItemRating, updateFoodItemRating]
  );

  const foodTagButtons = useMemo(() => {
    if (!foodItemRating) {
      return null;
    }

    const { ratingPercent, tags } = foodItemRating;
    const foodTags = match(ratingPercent)
      .with(THUMB_DOWN_PERCENT, () => negativeFoodTags)
      .with(THUMB_MIDDLE_PERCENT, () => neutralFoodTags)
      .with(THUMB_UP_PERCENT, () => positiveFoodTags)
      .with(null, () => ignoreFoodTags)
      .exhaustive()
      .filter((foodTag) => (hasBeenRated ? tags.includes(foodTag) : true));

    if (foodTags.length === 0) return null;

    return foodTags.map((tag) => {
      const isSelected = tags.includes(tag);

      const tagClasses = [
        'flex h-12 items-center justify-center rounded-md px-4 text-sm font-medium transition-colors duration-200',
        isSelected
          ? 'border border-eggplant-500 bg-eggplant-500 text-milk hover:bg-eggplant hover:border-eggplant'
          : 'border-2 border-eggplant-50 bg-milk text-eggplant-500 hover:border-eggplant-500 hover:bg-eggplant-50',
      ].join(' ');

      return (
        <button type='button' key={tag} onClick={() => handleTagClick(tag)} className={tagClasses}>
          {tag}
        </button>
      );
    });
  }, [
    hasBeenRated,
    foodItemRating,
    handleTagClick,
    ignoreFoodTags,
    neutralFoodTags,
    negativeFoodTags,
    positiveFoodTags,
  ]);

  return (
    <GroupOrderFoodItemRating
      foodItem={foodItem}
      onRatingClick={handleRatingClick}
      selectedRatingPercent={foodItemRating?.ratingPercent}>
      {foodTagButtons && foodTagButtons.length > 0 ? (
        <div className='mt-4 flex flex-wrap gap-2'>{foodTagButtons}</div>
      ) : null}
    </GroupOrderFoodItemRating>
  );
};
